import moment from 'moment' // date formatter
import './mediacontrols.css'
import L from 'leaflet'
import { useMap } from 'react-leaflet'
import { useSelector } from 'react-redux'
import { useEffect, useState, useRef } from 'react'
import { WetDogWeatherLayersEnum } from '../../../interfaces/WetDogWeatherLayers'
//
// Handles time scrubber at the bottom, play functionality, and displaying date
//

function MediaControls({
  curTime,
  setCurTime,
  isPlaying,
  timeRange,
  setIsPlaying,
  // Real tile timestamps read out of the data manifest (see WetDogWeatherLayers'
  // loadCallback, wired up for every layer). When a layer blends multiple
  // models with different refresh cadences (e.g. a 20-min high-res model
  // near-term + an hourly model further out), this is the only accurate
  // source of "what times actually have tiles" - it naturally contains dense
  // steps where the fast model covers the range and sparse steps beyond it.
  // Empty only if no manifest has arrived yet, in which case we fall back to
  // the fixed-interval generator.
  availableTimes = [] as number[],
}) {
  const map = useMap()
  const sliderRef = useRef(null)
  const bubbleRef = useRef(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const trackRef = useRef<HTMLDivElement>(null)
  const [sliderValue, setSliderValue] = useState(0)
  const [displayValue, setDisplayValue] = useState<String | null>(null)
  const [trackWidth, setTrackWidth] = useState(0)
  const curLayer = useSelector((state) => state.WetDogWeatherProducts.curLayer)
  // The position the scrubber opened at (usually "now") isn't necessarily a
  // real tile time itself, so without pinning it, dragging away and back
  // would snap onto whatever real tick is nearest instead of landing back on
  // the exact starting position. Captured once per layer selection.
  const startTimeRef = useRef<number | null>(null)
  useEffect(() => {
    startTimeRef.current = null
  }, [curLayer.layer])
  useEffect(() => {
    if (startTimeRef.current === null && curTime !== Number.NEGATIVE_INFINITY) {
      startTimeRef.current = curTime
    }
  }, [curTime])
  const [minRange, setMinRange] = useState(0)
  const [maxRange, setMaxRange] = useState(10000)

  // This whole widget sits physically inside the Leaflet map container (it's
  // rendered as a plain child of MapContainer, not a real L.Control), and
  // WetDogWeatherLayers registers its own `map.on('click', ...)` handler that
  // queries/pops up a value at the click point. Without this, any click here
  // that doesn't land on an element with its own handler bubbles up to that
  // map click listener - stealing the click and opening a query popup instead
  // of interacting with the slider. Same fix already used in OpacitySlider.ts.
  useEffect(() => {
    if (!containerRef.current) return
    L.DomEvent.disableClickPropagation(containerRef.current)
    L.DomEvent.disableScrollPropagation(containerRef.current)
  }, [])

  useEffect(() => {
    if (!trackRef.current) return
    const el = trackRef.current
    const observer = new ResizeObserver((entries) => {
      setTrackWidth(entries[0].contentRect.width)
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  // let minRange = 0
  // Date we're showing

  const date = new Date(curTime * 1000)

  const displayDate = moment
    .utc(Number(date.getTime()))
    .format('dddd M-D-yy | HH:mm')

  useEffect(() => {
    setMaxRange(10000)
    setMinRange(0)
  }, [curLayer.layer])

  const range = Number(timeRange[1]) - Number(timeRange[0])
  const scrubberValue =
    ((Number(curTime) - Number(timeRange[0])) / range) * 10000

  if (
    (curLayer.layer === WetDogWeatherLayersEnum.RADAR ||
      curLayer.layer === WetDogWeatherLayersEnum.MRMS ||
      curLayer.layer === WetDogWeatherLayersEnum.XWEATHER_RADAR ||
      curLayer.layer === WetDogWeatherLayersEnum.CASA_RADAR) &&
    scrubberValue < minRange &&
    scrubberValue > -2000
  ) {
    setMinRange(scrubberValue)
  }

  // Given an arbitrary time and the list of times we can actually request tiles
  // for, return whichever entry in that list is nearest. Used to snap slider
  // drags onto a real tile timestamp instead of an arbitrary second.
  function getClosestTick(time: number, tickList: number[]) {
    return tickList.reduce(
      (closest, t) => (Math.abs(t - time) < Math.abs(closest - time) ? t : closest),
      tickList[0]
    )
  }

  const handleChange = (e) => {
    setIsPlaying(false)
    // Raw time implied by where on the 0-10000 track the user dragged to.
    const rawTime =
      (e.target.value / 10000) * (timeRange[1] - timeRange[0]) + timeRange[0]
    // Snap onto the nearest real tile time (`ticks`, defined below) so the
    // request we actually make matches an available time slice rather than
    // whatever second the drag landed on.
    setCurTime(ticks.length > 0 ? getClosestTick(rawTime, ticks) : rawTime)
  }

  function onPlayClick() {
    setIsPlaying(!isPlaying)
  }

  function generateHourlyTicks(start: number, end: number, interval: number) {
    const ticks: number[] = []
    // round start to nearest hour
    let t = Math.ceil(start / interval) * interval
    for (; t <= end; t += interval) {
      ticks.push(t)
    }
    return ticks
  }

  function toSliderPercent(time: number, start: number, end: number) {
    return ((time - start) / (end - start)) * 100
  }

  // Prefer the real manifest-derived times whenever we have them - they're
  // ground truth for what tiles exist, including any blend of fast/slow
  // models. Only fall back to the guessed fixed-interval grid if no manifest
  // has arrived yet.
  const baseTicks =
    availableTimes.length > 0
      ? [...availableTimes]
      : curLayer.layer === WetDogWeatherLayersEnum.RADAR ||
        curLayer.layer === WetDogWeatherLayersEnum.MRMS ||
        curLayer.layer === WetDogWeatherLayersEnum.XWEATHER_RADAR ||
        curLayer.layer === WetDogWeatherLayersEnum.CASA_RADAR
      ? generateHourlyTicks(Number(timeRange[0]), Number(timeRange[1]), 600)
      : generateHourlyTicks(Number(timeRange[0]), Number(timeRange[1]), 1200) // every hour
  // Always keep the scrubber's opening position reachable, even though it's
  // usually "now" and not itself a real tile time.
  if (
    startTimeRef.current !== null &&
    !baseTicks.includes(startTimeRef.current)
  ) {
    baseTicks.push(startTimeRef.current)
  }
  const ticks = baseTicks.sort((a, b) => a - b)

  // Approx rendered width (px) of an "HH:mm" label plus breathing room.
  // Ticks come from the real manifest and aren't evenly spaced (dense near
  // one model's coverage, sparse beyond it), so picking labels by index
  // (every Nth tick) still lets nearby labels collide in the dense stretch.
  // Instead space labels by actual pixel distance along the track, using the
  // measured track width so it adapts to how wide the slider actually is.
  const LABEL_MIN_PX = 46
  const minGapPercent =
    trackWidth > 0 ? (LABEL_MIN_PX / trackWidth) * 100 : 8

  const labelTicks = new Set<number>()
  if (ticks.length > 0) {
    labelTicks.add(ticks[0])
    let lastPercent = toSliderPercent(ticks[0], timeRange[0], timeRange[1])
    for (let i = 1; i < ticks.length; i++) {
      const percent = toSliderPercent(ticks[i], timeRange[0], timeRange[1])
      if (percent - lastPercent >= minGapPercent) {
        labelTicks.add(ticks[i])
        lastPercent = percent
      }
    }
  }

  // Thin the rendered dash marks the same way, but with a much tighter pixel
  // threshold than labels (a dash is a couple px wide, not a text label).
  // Where two model cadences overlap (e.g. a 15-min grid blended with a
  // 20-min grid) real ticks land every 5-10 min, which rendered as a solid
  // blur with no visible separation. Snapping still uses the full `ticks`
  // array, so dragging can land on any real tick even if its own dash isn't
  // drawn. Always keep labeled ticks and the pinned start position visible.
  const TICK_MIN_PX = 6
  const tickGapPercent = trackWidth > 0 ? (TICK_MIN_PX / trackWidth) * 100 : 0.5
  const visibleTicks: number[] = []
  if (ticks.length > 0) {
    visibleTicks.push(ticks[0])
    let lastTickPercent = toSliderPercent(ticks[0], timeRange[0], timeRange[1])
    for (let i = 1; i < ticks.length; i++) {
      const t = ticks[i]
      const percent = toSliderPercent(t, timeRange[0], timeRange[1])
      if (
        percent - lastTickPercent >= tickGapPercent ||
        labelTicks.has(t) ||
        t === startTimeRef.current
      ) {
        visibleTicks.push(t)
        lastTickPercent = percent
      }
    }
  }

  //Adding a red marker to denote current Time
  const now = new Date().getTime() / 1000
  const curPercent = toSliderPercent(now, timeRange[0], timeRange[1])

  // Setting the scrubber value when this component re-renders.
  // Making sure the icon is correct.
  // This logic is necessary incase the MediaControls need to de-render and re-render because of fullscreen.

  const darkMode = useSelector((state) => state.mapState.darkMode)
  const icon = !isPlaying ? (
    <svg
      className={`w-8 h-8 ${darkMode ? 'text-white' : 'text-black'}`}
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
      fill="currentColor"
      viewBox="0 0 14 16"
    >
      <path d="M0 .984v14.032a1 1 0 0 0 1.506.845l12.006-7.016a.974.974 0 0 0 0-1.69L1.506.139A1 1 0 0 0 0 .984Z" />
    </svg>
  ) : (
    <svg
      className={`w-8 h-8 ${darkMode ? 'text-white' : 'text-black'}`}
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
      fill="currentColor"
      viewBox="0 0 12 16"
    >
      <path d="M3 0H2a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2Zm7 0H9a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2Z" />
    </svg>
  )

  //TODO: create a function to calculate hourly intervals between time ranges
  //TODO: create datalists for the above timestamps and display on the timeline
  // Called when the user clicks the play or pause button
  // TODO: We don't have to make this a toggle.  We could make it deterministic

  return (
    <>
      <div
        ref={containerRef}
        className={`flex flex-col rounded-md h-full w-full whitespace-nowrap ${
          darkMode ? 'bg-navigationBg' : 'bg-white'
        } p-6 rounded-md`}
        onMouseOver={() => map.dragging.disable()}
        onMouseOut={() => map.dragging.enable()}
      >
        <div className="flex flex-row h-full rounded-md justify-center items-center text-center relative">
          <button
            className="mr-1 w-8 h-8 rounded-full text-center m-2"
            onClick={() => onPlayClick()}
          >
            {icon}
          </button>
          <div
            className={
              'relative w-full h-1/2 flex flex-row justify-center items-center'
            }
          >
            <div
              className={`range-value w-auto p-4 m-2 h-12 -translate-y-10 flex flex-row
            justify-center items-center text-xl text-center font-semibold tracking-wide
            leading-6 rounded-lg ${darkMode ? 'text-white' : 'text-black'}`}
            >
              {`${displayDate} UTC`}
            </div>
            <div className="w-full relative" ref={trackRef}>
              <input
                type="range"
                min={0}
                max={10000}
                value={scrubberValue}
                id="scrubber-range"
                onChange={(e) => handleChange(e)}
                // Fine-grained on purpose: getClosestTick does the real
                // snapping to an actual tile time. A coarse step here (this
                // used to be 100, i.e. 1% of the track) quantizes the raw
                // drag position BEFORE that snap ever runs - over a ~48h+
                // range, 1% is ~29 min, coarser than the real tick spacing
                // (10-20 min), so several real ticks between two adjacent
                // native positions become unreachable by dragging.
                step="1"
                className="relative z-10 w-full h-1 bg-blue-500 rounded-lg cursor-pointer"
                list="markers"
                ref={sliderRef}
              />
              <div
                className={`absolute left-0 right-0 top-4 text-xs pointer-events-none ${
                  darkMode ? 'text-white' : 'text-black'
                }`}
              >
                {visibleTicks.map((t, idx) => {
                  const left = `${toSliderPercent(
                    t,
                    timeRange[0],
                    timeRange[1]
                  )}%`
                  return (
                    <div
                      key={idx}
                      className={`absolute flex flex-col items-center pointer-events-auto ${
                        darkMode ? 'text-white' : 'text-black'
                      }`}
                      style={{ left, transform: 'translateX(-50%)' }}
                      onClick={() => {
                        setIsPlaying(false) // stop playback if snapping
                        setCurTime(t) // jump to this exact timestamp
                      }}
                    >
                      <div
                        className={`w-0.5 h-2 ${
                          darkMode ? 'bg-white' : 'bg-black'
                        }`}
                      />{' '}
                      {/* tick */}
                      {labelTicks.has(t) && (
                        <span>{moment.unix(t).utc().format('HH:mm')}</span>
                      )}
                      {/* label */}
                    </div>
                  )
                })}
                <div
                  className="absolute flex flex-col items-center z-10"
                  style={{
                    left: `${curPercent}%`,
                    transform: 'translateY(-100%)',
                  }}
                >
                  <div className="w-1 h-2 bg-red-600 rounded" />{' '}
                  {/* big red tick */}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default MediaControls
